import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Inject,
    OnInit,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    IconButtonComponent,
    LiveStatusComponent,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { ConfirmDialogService } from '@interacta-shared/ui-common';
import { IAttachment } from '@interacta/util-post';
import * as PostApiActions from '@modules/communities/store/post/post-api.actions';
import { CommentUploadService } from '@modules/post-comment/services/comment-upload.service';
import { AttachmentInputService } from '@modules/shared-v2/services/attachment-input.service';
import * as UploadActions from '@modules/upload-dialog/store/upload.actions';
import { getUploadFeature } from '@modules/upload-dialog/store/upload.selector';
import { UPLOAD_ATTACHMENT_INPUT_SERVICE } from '@modules/upload-dialog/upload-dialog.module';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, merge } from 'rxjs';
import { distinctUntilKeyChanged, map, tap } from 'rxjs/operators';
import { UploadAttachmentItemComponent } from '../upload-attachment-item/upload-attachment-item.component';

@Component({
    selector: 'interacta-upload-dialog',
    templateUrl: './upload-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        IconButtonComponent,
        LiveStatusComponent,
        UploadAttachmentItemComponent,
        SeparatorComponent,
        TranslatePipe,
    ],
})
export class UploadDialogComponent implements OnInit {
    private store = inject(Store);
    private actions = inject(Actions);
    private confirmDialogService = inject(ConfirmDialogService);
    private commentUploadService = inject(CommentUploadService);
    private destroyRef = inject(DestroyRef);

    /** Post attachment uploads (popup context, persisted to the post). */
    readonly attachments = signal<IAttachment[]>([]);
    readonly uploadState = this.store.selectSignal(getUploadFeature);

    /** Post uploads + comment uploads (comment ones are read-only progress here). */
    readonly displayedAttachments = computed(() => [
        ...this.attachments(),
        ...this.commentUploadService.commentUploads(),
    ]);

    readonly isDialogCompact = computed(
        () => this.uploadState().isDialogCompact,
    );

    readonly notCanceledAttachmentsCount = computed(
        () => this.displayedAttachments().filter((i) => !i.isCanceled).length,
    );
    readonly completedUploads = computed(
        () =>
            this.displayedAttachments().filter(
                (i) => !i.isPartial && !i.isLoadingError && !i.isCanceled,
            ).length,
    );
    readonly hasPendingUploads = computed(() =>
        this.displayedAttachments().some((i) => i.isPartial),
    );

    private readonly displayedAttachments$ = toObservable(
        this.displayedAttachments,
    );

    constructor(
        @Inject(UPLOAD_ATTACHMENT_INPUT_SERVICE)
        public attachmentInputService: AttachmentInputService,
    ) {
        this.attachmentInputService.init({
            context: 'popup',
        });
    }

    ngOnInit(): void {
        this.subscribeToStartLoadingAttachment();
        this.subscribeToItemUpdates();

        this.subscribeToAddAttachmentSuccess();
        this.subscribeToAddAttachmentError();
        this.subscribeToAutoCloseWhenEmpty();
    }

    expand(): void {
        this.store.dispatch(UploadActions.expandDialog());
    }

    collapse(): void {
        this.store.dispatch(UploadActions.collapseDialog());
    }

    close(): void {
        this.attachments.set([]);
        this.commentUploadService.clearPublishedAttachments();
        this.store.dispatch(UploadActions.closeDialog());
    }

    retryUpload(index: number, attachment: IAttachment): void {
        if (this.commentUploadService.owns(attachment)) {
            // Comment uploads are managed by their own session; no retry here.
            return;
        }
        if (!attachment.fileItem) {
            throw new Error(
                "Can't retry upload for undefined attachment.fileItem ",
            );
        }

        this.attachments.update((curr) =>
            curr.map((i, idx) =>
                idx == index
                    ? {
                          ...i,
                          markAsHandled: false,
                          fileItem: undefined,
                      }
                    : i,
            ),
        );
        this.attachmentInputService.startLoadingProcedure([
            attachment.fileItem,
        ]);
    }

    cancelUpload(attachment: IAttachment): void {
        if (this.commentUploadService.owns(attachment)) {
            this.commentUploadService.cancelAttachment(attachment);
        } else {
            this.cancelAttachments([attachment]);
        }
    }

    async cancelNotYetLoadedUploads(): Promise<void> {
        const confirm = await firstValueFrom(
            this.confirmDialogService.open({
                title: 'UPLOAD.CANCEL_ALL_CONFIRM.TITLE',
                description: 'UPLOAD.CANCEL_ALL_CONFIRM.DESCRIPTION',
                confirm: 'UPLOAD.CANCEL_ALL_CONFIRM.CONFIRM',
                cancel: 'UPLOAD.CANCEL_ALL_CONFIRM.CANCEL',
                size: 'large',
            }),
        );

        if (confirm) {
            const attachments = this.attachments().filter((i) => i.isPartial);
            this.cancelAttachments(attachments);
            this.commentUploadService.cancelAllPendingUploads();
        }
    }

    private subscribeToAutoCloseWhenEmpty(): void {
        this.displayedAttachments$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((list) => {
                if (list.length === 0 && this.uploadState().isDialogOpen) {
                    this.close();
                }
            });
    }

    private cancelAttachments(attachments: IAttachment[]): void {
        this.attachmentInputService.cancelAttachmentsUploading(attachments);
    }

    private getAttachmentInError(
        oldList: IAttachment[],
        newItem: IAttachment,
    ): IAttachment | undefined {
        return oldList.find(
            (a) =>
                a.isLoadingError === true &&
                a.name === newItem.name &&
                a.markAsHandled === false,
        );
    }

    private updateAttachmentFromList(
        newItem: IAttachment,
        actualList: IAttachment[],
    ): IAttachment[] {
        return actualList.map((i) =>
            i.isPartial && i.name === newItem.name ? newItem : i,
        );
    }

    private subscribeToStartLoadingAttachment(): void {
        this.attachmentInputService.startLoadingAttachmentEvent$
            .pipe(
                tap(() => this.store.dispatch(UploadActions.openDialog())),
                tap((newAttachments) => {
                    this.store.dispatch(
                        UploadActions.updateSessionAttachmentsMap({
                            sessionId:
                                newAttachments[0].attachmentsUploadSessionId,
                            counter: newAttachments.length,
                        }),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((newAttachments) => {
                const isReload =
                    newAttachments.length === 1 &&
                    !!this.getAttachmentInError(
                        this.attachments(),
                        newAttachments[0],
                    );

                let newList: IAttachment[];
                if (isReload) {
                    const attachmentToReplace = this.getAttachmentInError(
                        this.attachments(),
                        newAttachments[0],
                    );
                    newList = this.attachments().map((i) =>
                        i == attachmentToReplace ? newAttachments[0] : i,
                    );
                } else {
                    newList = [...this.attachments(), ...newAttachments];
                }
                this.attachments.set(newList);
            });
    }

    private subscribeToItemUpdates(): void {
        merge(
            this.attachmentInputService.singleAttachmentLoadedEvent$,
            this.attachmentInputService.progressItemEvent$.pipe(
                distinctUntilKeyChanged('uploadProgress'),
            ),
        )
            .pipe(
                map((newItem) =>
                    this.updateAttachmentFromList(newItem, this.attachments()),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((list) => this.attachments.set(list));
    }

    private subscribeToAddAttachmentSuccess(): void {
        this.actions
            .pipe(
                ofType(PostApiActions.addAttachmentsSuccess),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ attachments }) => {
                this.attachments.update((curr) =>
                    curr.map((actualItem) => {
                        const itemToUpdate = attachments.find(
                            (a) => a.contentRef === actualItem.contentRef,
                        );
                        return itemToUpdate
                            ? {
                                  ...actualItem,
                                  id: itemToUpdate.id,
                              }
                            : actualItem;
                    }),
                );
            });
    }

    private subscribeToAddAttachmentError(): void {
        this.actions
            .pipe(
                ofType(PostApiActions.addAttachmentsError),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ attachments }) => {
                this.attachments.update((curr) =>
                    curr.map((actualItem) => {
                        const itemToUpdate = attachments.find(
                            (a) => a.contentRef === actualItem.contentRef,
                        );
                        return itemToUpdate
                            ? {
                                  ...actualItem,
                                  isLoadingError: true,
                                  uploadProgress: undefined,
                              }
                            : actualItem;
                    }),
                );
            });
    }
}
